// CreateApp.js - Modern Onboarding UI (Dynamic Steps)
import React, { useState, useEffect, useCallback } from "react";
import createAppStyle from "./CreateApp.module.css";

import { FaInfoCircle } from "react-icons/fa";
import modalStyles from "./Modal.module.css";
import { useHistory } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import AlertPopup from "../AlertPopup";
import { useCreateApp } from "../../hooks/useCreateApp";
import { useSelector } from "react-redux";
import { GrFormNextLink, GrFormPreviousLink } from "react-icons/gr";
import { FaCheck } from "react-icons/fa6";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

// ============================================================
// GLOBAL FLAG: SKIP "WHAT TO BUILD" STEP
// ============================================================
// Set this to `false` when you want to explicitly show the 
// "What to build" step. Currently `true` means we skip it.
// ============================================================
const DONT_SHOW_WHAT_TO_BUILD_STEP_IN_APP_CREATION = true;

// ─────────────────────────────────────────────────────────────
// Step Indicator
// ─────────────────────────────────────────────────────────────
const StepIndicator = ({ steps, currentStep, onStepClick }) => {
  return (
    <div className={createAppStyle.stepIndicatorContainer}>
      {steps.map((step, index) => {
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;

        return (
          <div key={step.key} className={createAppStyle.stepWrapper}>
            <div
              className={`${createAppStyle.stepItem} 
                ${isActive ? createAppStyle.stepActive : ""} 
                ${isCompleted ? createAppStyle.stepCompleted : ""}`}
              onClick={() => onStepClick(index)}
            >
              <div className={createAppStyle.stepNumber}>
                {isCompleted ? <FaCheck /> : index + 1}
              </div>
              <div className={createAppStyle.stepLabel}>{step.label}</div>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`${createAppStyle.stepConnector} 
                ${isCompleted ? createAppStyle.connectorCompleted : ""}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Modal Component
// ─────────────────────────────────────────────────────────────
export const Modal = ({
  isOpen,
  onClose,
  content,
  hideCloseButton = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className={modalStyles.modalOverlay}>
      <div className={modalStyles.modal}>
        {!hideCloseButton && (
          <button className={modalStyles.closeButton} onClick={onClose}>
            <IoClose size={20} />
          </button>
        )}
        <div className={modalStyles.modalContent}>{content}</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Step 2 — Setup Intent Questionnaire
// ─────────────────────────────────────────────────────────────
const AppQuestionnaireSection = ({
  setupIntent,
  setSetupIntent,
  openInfoKey,
  toggleInfo,
}) => {
  const update = (key, value) => {
    setSetupIntent((prev) => ({ ...prev, [key]: value }));
  };

  const toggleMulti = (key) => {
    setSetupIntent((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className={createAppStyle.questionnaireSection}>
      <div className={createAppStyle.questionnaireHeader}>
        <h4>What should your chat app do for you?</h4>
        <p>
          Pick everything you'd like. We'll help you set each one up right after
          the app is created — nothing here locks you in, and you can add more
          later.
        </p>
      </div>

      {/* ── Q1 ── */}
      <div className={createAppStyle.questionTile}>
        <div className={createAppStyle.questionLabelRow}>
          <div>
            <label className={createAppStyle.questionLabel}>
              What should your app help with?
              <span className={createAppStyle.requiredStar}> *</span>
            </label>
            <p className={createAppStyle.questionSubtext}>
              Select everything that applies. At least one is required.
            </p>
          </div>
          <button
            type="button"
            className={createAppStyle.questionInfoBtn}
            onClick={() => toggleInfo("q1")}
            aria-expanded={openInfoKey === "q1"}
          >
            <FaInfoCircle />
            <span>See what's included</span>
          </button>
        </div>

        {openInfoKey === "q1" && (
          <div className={createAppStyle.questionInfoPanel}>
            <p>
              <strong>🎯 Capture leads</strong>
            </p>
            <p>
              Adds a simple form inside the chat. Visitors share their name,
              email, phone, or anything else you'd like to collect. Every
              submission lands in your portal under <em>LeadGen & FAQ</em>,
              where you can review and export them.
            </p>
            <p>
              Want to go further? You can wire your lead pipeline into the{" "}
              <strong>AI Agent</strong> — the agent captures the lead{" "}
              <em>and</em> then does something with it: sends it to your CRM,
              notifies a Slack channel, or triggers any custom flow you've set
              up on your own servers.
            </p>

            <p style={{ marginTop: "16px" }}>
              <strong>💡 Answer questions automatically</strong>
            </p>
            <p>Two ways to answer, and you can choose one now, change later:</p>
            <ul>
              <li>
                <strong>Simple FAQ</strong> — a clickable tree of questions
                you've prepared.
              </li>
              <li>
                <strong>AI answers</strong> — reads your website, docs, PDFs,
                and answers in its own words, even for questions you didn't
                anticipate.
              </li>
            </ul>
            <p>
              Want the AI to answer with <strong>real data</strong>? The AI
              Agent can be attached to your bot — so instead of guessing at
              "what's your refund policy?" it can look up{" "}
              <em>this specific visitor's</em> order and tell them exactly. The
              agent becomes the brain that connects your chatbot to your own
              systems.
            </p>

            <p style={{ marginTop: "16px" }}>
              <strong>💬 Hand off to a human</strong>
            </p>
            <p>
              When a visitor needs a real person, the chat routes to your team.
              You'll see the conversation in your <em>All Chats</em> inbox,
              reply in real time, and share files. Each agent sees only the apps
              you assign them.
            </p>

            <p style={{ marginTop: "16px" }}>
              <strong>⚡ Let the AI Agent take real action</strong>
            </p>
            <p>
              The AI Agent goes beyond answering — it understands{" "}
              <strong>what the visitor wants</strong> and then{" "}
              <strong>does it</strong> by calling your systems. Examples:
            </p>
            <ul>
              <li>
                <em>"Can I get a refund for order 412?"</em> — the agent looks
                up the order and either starts the refund or explains why it
                can't.
              </li>
              <li>
                <em>"Is a room available on Friday?"</em> — the agent checks
                your booking system and offers open slots.
              </li>
              <li>
                <em>"Update my delivery address"</em> — the agent updates it, in
                the conversation.
              </li>
            </ul>
            <p>
              <strong>You stay in control</strong> — you decide what the agent
              can and can't do, and each visitor's session runs on their own
              permissions, not yours.
            </p>

            <p
              style={{
                marginTop: "16px",
                padding: "12px 14px",
                background: "#f0f7ff",
                borderRadius: "6px",
                borderLeft: "3px solid #3b82f6",
              }}
            >
              <strong>Under the hood, the platform handles the harder cases:</strong>
            </p>
            <ul>
              <li>
                <strong>Chained APIs</strong> — the agent can call one API, use
                the response to decide the next call, and continue chaining
                until the job is done. Perfect for multi-step flows like{" "}
                <em>check availability → book room → confirm with the visitor</em>.
              </li>
              <li>
                <strong>Authenticated APIs</strong> — if your APIs sit behind a
                login, we support that. The agent can authenticate on behalf of
                the visitor (or as a service) — including{" "}
                <strong>secondary auth</strong> like OTP verification when a
                step needs extra confirmation.
              </li>
              <li>
                <strong>Async APIs</strong> — if some of your systems can't
                reply in real time (a payment confirmation, a manual approval),
                we support <strong>webhook-based responses</strong>. The agent
                places the request, the visitor sees a "processing…" state, and
                when your system is ready, we resume the conversation
                automatically.
              </li>
            </ul>
            <p>
              You don't need any of this today. Set up what you need, add the
              rest when you're ready — everything lives in your app's{" "}
              <em>API Config</em>, <em>ChainApis</em>, and{" "}
              <em>Auth Integration</em> tabs.
            </p>
          </div>
        )}

        <div className={createAppStyle.choiceRow}>
          {[
            {
              key: "leadgen",
              icon: "🎯",
              title: "Capture leads",
              hint:
                "Save visitor details so your team can follow up. Stored in your portal by default.",
            },
            {
              key: "auto_answers",
              icon: "💡",
              title: "Answer questions automatically",
              hint:
                "Instant answers for visitors — from your FAQs, website, docs, or PDFs.",
            },
            {
              key: "handoff",
              icon: "💬",
              title: "Hand off to a human",
              hint:
                "Let a real person step in when the AI can't help, or when a visitor asks for one.",
            },
            {
              key: "agentic",
              icon: "⚡",
              title: "Let the AI Agent take real action",
              hint:
                "Give the AI Agent the ability to do things — check orders, book appointments, update records — through your own systems.",
            },
          ].map((opt) => (
            <label
              key={opt.key}
              className={`${createAppStyle.choiceCard} ${setupIntent[opt.key] ? createAppStyle.choiceCardActive : ""
                }`}
            >
              <input
                type="checkbox"
                checked={!!setupIntent[opt.key]}
                onChange={() => toggleMulti(opt.key)}
              />
              <span className={createAppStyle.choiceIcon}>{opt.icon}</span>
              <span className={createAppStyle.choiceTitle}>{opt.title}</span>
              <span className={createAppStyle.choiceHint}>{opt.hint}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ── Q2 — only if auto_answers selected ── */}
      {setupIntent.auto_answers && (
        <div className={createAppStyle.questionTile}>
          <div className={createAppStyle.questionLabelRow}>
            <div>
              <label className={createAppStyle.questionLabel}>
                How do you want to answer questions?
              </label>
              <p className={createAppStyle.questionSubtext}>
                You can change this anytime. Start simple, upgrade later.
              </p>
            </div>
          </div>

          <div className={createAppStyle.choiceRow}>
            {[
              {
                value: "faq",
                title: "Simple FAQ mode",
                hint: "Clickable question tree. No AI, no surprises.",
              },
              {
                value: "ai",
                title: "AI answers from my content",
                hint:
                  "Reads your site, docs, and PDFs — answers in its own words.",
              },
              {
                value: "ai_systems",
                title: "AI answers + calls my own systems",
                hint:
                  "Same as above, plus live lookups in your own systems.",
              },
            ].map((opt) => (
              <label
                key={opt.value}
                className={`${createAppStyle.choiceCard} ${setupIntent.auto_answers_mode === opt.value
                    ? createAppStyle.choiceCardActive
                    : ""
                  }`}
              >
                <input
                  type="radio"
                  name="auto_answers_mode"
                  value={opt.value}
                  checked={setupIntent.auto_answers_mode === opt.value}
                  onChange={(e) => update("auto_answers_mode", e.target.value)}
                />
                <span className={createAppStyle.choiceTitle}>{opt.title}</span>
                <span className={createAppStyle.choiceHint}>{opt.hint}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* ── Q3 — only if agentic selected ── */}
      {setupIntent.agentic && (
        <div className={createAppStyle.questionTile}>
          <div className={createAppStyle.questionLabelRow}>
            <div>
              <label className={createAppStyle.questionLabel}>
                Do you want the AI Agent to connect to your own systems?
              </label>
              <p className={createAppStyle.questionSubtext}>
                Nothing to prepare today — this just tells us whether to include
                API setup in your checklist.
              </p>
            </div>
          </div>

          <div className={createAppStyle.choiceRow}>
            {[
              {
                value: "ready",
                title: "Yes, I have my API details ready",
                hint: "We'll guide you right after creation.",
              },
              {
                value: "later",
                title: "Yes, but I'll set it up later",
                hint: "We'll add it to your setup checklist.",
              },
              {
                value: "unsure",
                title: "Not sure yet — help me decide",
                hint:
                  "We'll create the app and show you what's involved.",
              },
            ].map((opt) => (
              <label
                key={opt.value}
                className={`${createAppStyle.choiceCard} ${setupIntent.agentic_readiness === opt.value
                    ? createAppStyle.choiceCardActive
                    : ""
                  }`}
              >
                <input
                  type="radio"
                  name="agentic_readiness"
                  value={opt.value}
                  checked={setupIntent.agentic_readiness === opt.value}
                  onChange={(e) =>
                    update("agentic_readiness", e.target.value)
                  }
                />
                <span className={createAppStyle.choiceTitle}>{opt.title}</span>
                <span className={createAppStyle.choiceHint}>{opt.hint}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Right-side Questions Section (dispatcher)
// ─────────────────────────────────────────────────────────────
export const QuestionsSec = ({
  selectedKey,
  setChatAppName,
  botContent,
  setBotContent,
  setupIntent,
  setSetupIntent,
  openInfoKey,
  toggleInfo,
  industriesList,
  subIndustriesMap,
}) => {
  const [nameError, setNameError] = useState("");
  const [isLocked, setIsLocked] = useState(false);

  const handleKeyDown = (e) => {
    if (!isLocked) return;
    if (e.key === "Backspace" || e.key === "Delete") {
      setIsLocked(false);
      return;
    }
    e.preventDefault();
  };

  const handleNameChange = (e) => {
    if (isLocked) return;
    const newValue = e.target.value;
    const allowedPattern = /^[A-Za-z0-9_-]*$/;
    if (!allowedPattern.test(newValue)) {
      setNameError(
        "Spaces and special characters are not allowed. Use A–Z, a–z, 0–9, underscore (_), hyphen (-)",
      );
      setIsLocked(true);
      return;
    }
    setNameError("");
    setChatAppName(newValue);
  };

  const handleIndustryChange = (e) => {
    const newIndustry = e.target.value;
    setBotContent((prev) => ({
      ...prev,
      industry: newIndustry,
      subIndustry: "",
    }));
  };

  const handleSubIndustryChange = (e) => {
    setBotContent((prev) => ({
      ...prev,
      subIndustry: e.target.value,
    }));
  };

  // Step 1 — Name, Industry & Region
  if (selectedKey === "Choose_A_Name_for_your_chat_app") {
    return (
      <div className={createAppStyle.nameRegionContainer}>
        {/* App Name */}
        <div className={createAppStyle.formGroup}>
          <label className={createAppStyle.formLabel}>
            App Name <span className={createAppStyle.requiredStar}>*</span>
          </label>
          <input
            type="text"
            placeholder="Enter a unique app name (e.g., my-chat-app)"
            onChange={handleNameChange}
            onKeyDown={handleKeyDown}
            className={createAppStyle.formInput}
          />
          {nameError && <p className={createAppStyle.errorText}>{nameError}</p>}
          <p className={createAppStyle.helperText}>
            Use letters, numbers, underscores (_), and hyphens (-). No spaces.
          </p>
        </div>

        {/* Industry */}
        <div className={createAppStyle.formGroup}>
          <label className={createAppStyle.formLabel}>
            Industry <span className={createAppStyle.requiredStar}>*</span>
          </label>
          <select
            value={botContent?.industry || ""}
            onChange={handleIndustryChange}
            className={createAppStyle.formSelect}
          >
            <option value="">Select your industry</option>
            {industriesList.map((ind) => (
              <option key={ind} value={ind}>
                {ind}
              </option>
            ))}
          </select>
          <p className={createAppStyle.helperText}>
            Choose the industry that best describes your business
          </p>
        </div>

        {/* Sub-Industry */}
        <div className={createAppStyle.formGroup}>
          <label className={createAppStyle.formLabel}>
            Sub-Industry <span className={createAppStyle.requiredStar}>*</span>
          </label>
          <select
            value={botContent?.subIndustry || ""}
            onChange={handleSubIndustryChange}
            disabled={!botContent?.industry}
            className={createAppStyle.formSelect}
          >
            <option value="">Select sub-industry</option>
            {botContent?.industry &&
              subIndustriesMap[botContent.industry]?.map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
          </select>
          <p className={createAppStyle.helperText}>
            {!botContent?.industry
              ? "Please select an industry first"
              : "Select your specific sub-industry for better AI agent tuning"}
          </p>
        </div>

        {/* Region */}
        <div className={createAppStyle.formGroup}>
          <label className={createAppStyle.formLabel}>
            Region <span className={createAppStyle.requiredStar}>*</span>
          </label>
          <div className={createAppStyle.regionOptions}>
            <label
              className={`${createAppStyle.regionOption} ${botContent?.region === "US" ? createAppStyle.regionSelected : ""
                }`}
            >
              <input
                type="radio"
                name="region"
                value="US"
                checked={botContent?.region === "US"}
                onChange={() =>
                  setBotContent((prev) => ({ ...prev, region: "US" }))
                }
              />
              🇺🇸 US
            </label>
            <label
              className={`${createAppStyle.regionOption} ${botContent?.region === "IN" ? createAppStyle.regionSelected : ""
                }`}
            >
              <input
                type="radio"
                name="region"
                value="IN"
                checked={botContent?.region === "IN"}
                onChange={() =>
                  setBotContent((prev) => ({ ...prev, region: "IN" }))
                }
              />
              🇮🇳 IN
            </label>
          </div>
          <p className={createAppStyle.helperText}>
            {botContent?.region
              ? `Selected: ${botContent.region === "US" ? "🇺🇸 United States" : "🇮🇳 India"
              } — Your app runs on our US-based servers. Pick the region closest to your main audience or team — this only affects latency, not access. Users from anywhere in the world can still reach your app.`
              : "Select a region to deploy your chat instance"}
          </p>
        </div>
      </div>
    );
  }

  // Step 2 — Setup Intent
  if (selectedKey === "Choose_Setup_Intent") {
    return (
      <AppQuestionnaireSection
        setupIntent={setupIntent}
        setSetupIntent={setSetupIntent}
        openInfoKey={openInfoKey}
        toggleInfo={toggleInfo}
      />
    );
  }

  return <div>Select an option from the left menu.</div>;
};

// ─────────────────────────────────────────────────────────────
// Main — CreateApp
// ─────────────────────────────────────────────────────────────
export default function CreateApp() {
  const history = useHistory();

  const {
    botContent,
    setBotContent,
    updating,
    setAlert,
    alert,
    resetBotContent,
  } = useCreateApp();

  const [appCreationState, setAppCreationState] = useState({
    // Hardcoded framework since we only support V2
    selectedFramework: "V2",
    chatAppName: "",
    selectedAppType: "P2A",
    selectedKey: "Choose_A_Name_for_your_chat_app", // Start at step 1
  });

  const { chatAppName, selectedAppType, selectedKey } = appCreationState;

  const [isDraft, setIsDraft] = useState(true);
  const [industriesList, setIndustriesList] = useState([]);
  const [subIndustriesMap, setSubIndustriesMap] = useState({});

  // ─────────────────────────────────────────────
  // Setup Intent — captured on Step 2, sent in meta_data
  // ─────────────────────────────────────────────
  const [setupIntent, setSetupIntent] = useState({
    leadgen: false,
    auto_answers: false,
    auto_answers_mode: "",
    handoff: false,
    agentic: false,
    agentic_readiness: "",
  });

  const [openInfoKey, setOpenInfoKey] = useState(null);
  const toggleInfo = (key) =>
    setOpenInfoKey((prev) => (prev === key ? null : key));

  // Fetch industries
  useEffect(() => {
    const fetchIndustries = async () => {
      const token = localStorage.getItem("token");
      const region = botContent?.region?.toLowerCase() || "us";
      const url = `https://${region}.autobot2.${backend_api_base_url}/ai_query_sys?task=global_intents`;

      try {
        const response = await fetch(url, {
          headers: { Authorization: token },
        });

        if (response.ok) {
          const json = await response.json();
          let fullData;
          if (json.body && typeof json.body === "string") {
            const parsedBody = JSON.parse(json.body);
            fullData = parsedBody.data;
          } else if (json.body && typeof json.body === "object") {
            fullData = json.body.data;
          } else if (json.data) {
            fullData = json.data;
          } else {
            setIndustriesList([]);
            setSubIndustriesMap({});
            return;
          }

          if (fullData && typeof fullData === "object") {
            const industries = Object.keys(fullData);
            setIndustriesList(industries);

            const subMap = {};
            industries.forEach((ind) => {
              const subs = Object.keys(fullData[ind] || {}).filter(
                (k) => k !== "default",
              );
              subMap[ind] = subs;
            });
            setSubIndustriesMap(subMap);
          } else {
            setIndustriesList([]);
            setSubIndustriesMap({});
          }
        } else {
          setIndustriesList([]);
          setSubIndustriesMap({});
        }
      } catch (err) {
        console.error("[ERROR] Error fetching industries:", err);
        setIndustriesList([]);
        setSubIndustriesMap({});
      }
    };

    fetchIndustries();
  }, [botContent?.region]);

  // Keep botContent in sync with app creation state
  useEffect(() => {
    if (botContent) {
      const meData = JSON.parse(localStorage.getItem("meData"));
      const kb_id = meData?.tenant_info?.tenant
        ? `${meData.tenant_info.tenant}__${chatAppName}`
        : "";

      setBotContent((prev) => ({
        ...prev,
        app_name: chatAppName,
        kb_id,
        settings: {
          ...prev.settings,
          version: "V2", // Hardcoded
          app_type: selectedAppType,
        },
      }));
    }
  }, [chatAppName, selectedAppType, setBotContent]);

  const tenantPlan = useSelector((state) => state.plan.plan);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
  };

  const handleSetChatAppName = useCallback((name) => {
    setAppCreationState((prev) => ({ ...prev, chatAppName: name }));
  }, []);

  const handleSetSelectedKey = useCallback((key) => {
    setAppCreationState((prev) => ({ ...prev, selectedKey: key }));
  }, []);

  const resetAppCreation = useCallback(() => {
    setAppCreationState({
      selectedFramework: "V2",
      chatAppName: "",
      selectedAppType: "P2A",
      selectedKey: "Choose_A_Name_for_your_chat_app",
    });
    setSetupIntent({
      leadgen: false,
      auto_answers: false,
      auto_answers_mode: "",
      handoff: false,
      agentic: false,
      agentic_readiness: "",
    });
    resetBotContent();
  }, [resetBotContent]);

  // ─────────────────────────────────────────────
  // Steps — dynamically filtered based on the global flag
  // ─────────────────────────────────────────────
  const allSteps = [
    {
      label: "Name & Region",
      key: "Choose_A_Name_for_your_chat_app",
      icon: "📝",
    },
    {
      label: "What to build",
      key: "Choose_Setup_Intent",
      icon: "🎯",
    },
  ];

  const stepDefinitions = DONT_SHOW_WHAT_TO_BUILD_STEP_IN_APP_CREATION
    ? allSteps.filter((step) => step.key !== "Choose_Setup_Intent")
    : allSteps;

  const currentStepIndex = stepDefinitions.findIndex(
    (s) => s.key === selectedKey,
  );
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === stepDefinitions.length - 1;

  const goToPreviousSection = () => {
    if (currentStepIndex > 0) {
      handleSetSelectedKey(stepDefinitions[currentStepIndex - 1].key);
    }
  };

  const goToStep = (index) => {
    if (index < currentStepIndex) {
      handleSetSelectedKey(stepDefinitions[index].key);
    }
  };

  const isButtonDisabled = () => {
    const currentKey = selectedKey;

    if (currentKey === "Choose_A_Name_for_your_chat_app") {
      if (!chatAppName || chatAppName.trim() === "") return true;
      if (!botContent?.industry) return true;
      if (!botContent?.subIndustry) return true;
      if (!botContent?.region) return true;
      return false;
    }

    if (currentKey === "Choose_Setup_Intent") {
      const hasAnyIntent =
        setupIntent.leadgen ||
        setupIntent.auto_answers ||
        setupIntent.handoff ||
        setupIntent.agentic;
      return !hasAnyIntent;
    }

    return true;
  };

  // Helper to post a minimal bot content skeleton after app creation.
  const postBotContent = async (token, payload, regionPre) => {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify(payload);
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    const region = regionPre.toLowerCase();
    try {
      const response = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/add_bot_content`,
        requestOptions,
      );
      const result = await response.text();
      console.log("Bot content creation result:", result);
      return result;
    } catch (error) {
      console.error("Error creating bot content:", error);
      throw error;
    }
  };

  const goToNextSection = async () => {
    if (isLastStep) {
      // Final step — Create App
      if (!chatAppName) {
        showAlert("error", "Please provide all the required answers!");
        return;
      }

      const token = localStorage.getItem("token");
      const meData = JSON.parse(localStorage.getItem("meData"));

      if (meData && meData.tenant_info?.tenant) {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const meta_data = {
          setup_intent: {
            leadgen: setupIntent.leadgen || false,
            auto_answers: setupIntent.auto_answers || false,
            auto_answers_mode: setupIntent.auto_answers_mode || null,
            handoff: setupIntent.handoff || false,
            agentic: setupIntent.agentic || false,
            agentic_readiness: setupIntent.agentic_readiness || null,
            captured_at: new Date().toISOString(),
          },
        };

        const api_payload = {
          app_name: chatAppName,
          tenant_id: meData.tenant_info.id,
          settings: {
            app_type: selectedAppType,
            version: "V2", // Hardcoded version
          },
          chat_settings: botContent?.chat_settings,
          ai_agent_bot_enabled: false,
          enabled_bot_content: false,
          session_enabled: false,
          dont_show_chat_box_at_logout: false,
          exclude_paths: botContent?.exclude_paths || [],
          live_agent_enabled: false,
          live_bot_enabled: false,
          ingestion_methods: botContent?.ingestion_methods,
          region: botContent?.region,
          role: botContent?.role,
          timestamp: Date.now(),
          allow_nested: false,
          explore_clickables: false,
          isTraining: 0,
          tenant_info: meData.tenant_info,
          draft: isDraft,
          live_settings: { WELCOME_MSG: "", OFFLINE_MSG: "" },
          industry: botContent?.industry || "",
          subIndustry: botContent?.subIndustry || "",
          custom_intent_ui_layout: "stack",
          meta_data,
        };

        const raw = JSON.stringify(api_payload);
        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow",
        };

        try {
          const region_prefix = botContent?.region.toLowerCase();
          const response = await fetch(
            `https://${region_prefix}.apps2.${backend_api_base_url}/add_app`,
            requestOptions,
          );

          const data = await response.json();

          if (!response.ok || data.statusCode >= 400) {
            let errorMessage = "Something went wrong.";
            try {
              const parsedBody = JSON.parse(data.body);
              if (parsedBody.message) errorMessage = parsedBody.message;
            } catch (parseError) {
              console.error("Failed to parse error body:", parseError);
            }
            showAlert("error", `${errorMessage}`);
            return;
          }

          if (
            data.ResponseMetadata &&
            data.ResponseMetadata.HTTPStatusCode === 200
          ) {
            // ─────────────────────────────────────────────
            // Always create a minimal bot content skeleton.
            // ─────────────────────────────────────────────
            try {
              const kb_id = `${meData.tenant_info.tenant}__${chatAppName}`;

              await postBotContent(
                token,
                {
                  app_name: chatAppName,
                  bot_payload: {
                    app_name: chatAppName,
                    WELCOME_MSG: "",
                    kb_id,
                    OFFLINE_MSG: "",
                    FAQ: null,
                  },
                  settings: {
                    version: selectedAppType,
                    app_type: "V2", // Hardcoded
                  },
                  timestamp: Date.now(),
                  tenant_id: meData.tenant_info.id,
                },
                api_payload.region,
              );
            } catch (botContentError) {
              console.error("Failed to create bot content skeleton:", botContentError);
              // Non-fatal — the app exists.
            }

            showAlert(
              "success",
              isDraft
                ? "Draft app saved! Complete it later from your apps list."
                : "App created successfully! Let's set it up.",
            );

            resetAppCreation();
            history.push(`/`);
          }
        } catch (error) {
          console.error("Error:", error);
          showAlert("error", "An unexpected error occurred.");
        }
      } else {
        showAlert(
          "error",
          "Unable to get tenant information. Please try again.",
        );
      }
    } else {
      // Move to next step
      if (currentStepIndex < stepDefinitions.length - 1) {
        handleSetSelectedKey(stepDefinitions[currentStepIndex + 1].key);
      }
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, show: false });
  };

  const buttonText = () => {
    if (updating) return "Creating...";
    if (isLastStep) {
      return isDraft ? "Save as Draft" : "Create Chat App";
    }
    return (
      <>
        Next <GrFormNextLink size={20} />
      </>
    );
  };

  return (
    <div className={createAppStyle.createContainer}>
      <div className={createAppStyle.header}>
        <h1 className={createAppStyle.mainTitle}>🚀 Launch Your AI Chat App</h1>
        <p className={createAppStyle.subTitle}>
          Set up your intelligent customer operations agent in minutes
        </p>
      </div>

      <div className={createAppStyle.mainContainer}>
        {/* Hide StepIndicator completely if there's only 1 step */}
        {stepDefinitions.length > 1 && (
          <div className={createAppStyle.stepsContainer}>
            <StepIndicator
              steps={stepDefinitions}
              currentStep={currentStepIndex}
              onStepClick={goToStep}
            />
          </div>
        )}

        <div className={createAppStyle.contentArea}>
          <div className={createAppStyle.contentWrapper}>
            <QuestionsSec
              selectedKey={selectedKey}
              setChatAppName={handleSetChatAppName}
              botContent={botContent}
              setBotContent={setBotContent}
              setupIntent={setupIntent}
              setSetupIntent={setSetupIntent}
              openInfoKey={openInfoKey}
              toggleInfo={toggleInfo}
              industriesList={industriesList}
              subIndustriesMap={subIndustriesMap}
            />
          </div>

          <div className={createAppStyle.navigationControls}>
            {!isFirstStep && (
              <button
                onClick={goToPreviousSection}
                className={createAppStyle.prevButton}
                disabled={updating}
              >
                <GrFormPreviousLink size={20} />
                Previous
              </button>
            )}

            {isLastStep && (
              <div className={createAppStyle.draftToggle}>
                <input
                  type="checkbox"
                  id="draftMode"
                  checked={isDraft}
                  onChange={(e) => setIsDraft(e.target.checked)}
                />
                <label htmlFor="draftMode">
                  Create as draft{" "}
                  <span className={createAppStyle.draftHint}>
                    (recommended — finish setup, then go live)
                  </span>
                </label>
              </div>
            )}

            <button
              onClick={goToNextSection}
              className={`${createAppStyle.nextButton} ${isButtonDisabled() ? createAppStyle.buttonDisabled : ""
                }`}
              disabled={isButtonDisabled() || updating}
            >
              {buttonText()}
            </button>
          </div>
        </div>
      </div>

      {alert.show && (
        <AlertPopup
          type={alert.type}
          message={alert.message}
          onClose={handleCloseAlert}
        />
      )}
    </div>
  );
}